import React, { useEffect, useMemo } from "react";
import { Stack, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";

import { displayError } from "../../../helpers";
import { DefaultAmountOptions } from "./DefaultAmountOptions";
import { DonationTypesComponent } from "./DonationTypesComponent";
import { ACH, CHEQUE, DONATION_IMPACT_TYPES } from "../../../constants";
import { useNonInitialEffect } from "../../../hooks/UseNonInitialEffect";
import { checkoutActions, donationActions } from "../../../store/actions";
import { CheckboxWithLabel, GoalAmountInput, MessageBox } from "../../../components";

function AmountSection({ state, isFundraiserCheckout, handleChange, validationErrors, pageDetails }) {
    const dispatch = useDispatch();

    const donationMessage = useSelector(state => state.donation.checkoutMessages.donation_message);
    const userLocationCurrencyExistsInDB = useSelector(state => state.check_if_country_partner_isactive_for_donor.if_partner_active_for_donor);
    const pageCurrencyExistsInDB = useSelector(state => state.check_if_country_partner_isactive_for_fundraiser.if_partner_active_for_fundraiser);

    const updateSelectedCurrency = (currency) => {
        dispatch(checkoutActions.updateCheckoutCurrency(currency));
    }

    const calculateDonationImpact = () => {
        const donationImapact = state.isRecurring ? DONATION_IMPACT_TYPES.monthly : DONATION_IMPACT_TYPES.annual
        dispatch(donationActions.calculateProductImpact(state.selectedCountry.currency.id, state.donationAmount, donationImapact))
    }

    const fetchCountryDependentValues = () => {
        dispatch(donationActions.getBanksList(state.selectedCountry.id));
        dispatch(donationActions.getPaymentMethods(state.selectedCountry.id));
        dispatch(donationActions.getTaxDonationMessage(state.selectedCountry.id));
        dispatch(donationActions.isCountryCorporateMatch(state.selectedCountry.id));
        dispatch(donationActions.checkIfCountryHasGiftAid(state.selectedCountry.id));
        dispatch(donationActions.getCurrencyDefaultAmounts(state.selectedCountry.id));
    }

    useEffect(() => {
        // below function resets the payment method if the selected method is cheque
        if (state.selectedPaymentMethod == CHEQUE || state.selectedPaymentMethod == ACH)
            dispatch(checkoutActions.resetCheckoutPaymentMethod());

        const timer = setTimeout(() => {
            calculateDonationImpact();
        }, 1000)

        return () => clearTimeout(timer)

    }, [state.donationAmount])

    useEffect(() => {
        calculateDonationImpact();

        if (state.selectedPaymentMethod == ACH && state.isRecurring)
            dispatch(checkoutActions.resetCheckoutPaymentMethod());

    }, [state.isRecurring])

    useEffect(() => {
        dispatch(checkoutActions.resetCountryDependentState());
        fetchCountryDependentValues();
    }, [state.selectedCountry.id])

    useNonInitialEffect(() => {
        /*

            If the currency of user current location is active by admin then the current location
            currency will be displayed in contact form and currency dropdown

        */
        if (userLocationCurrencyExistsInDB)
            dispatch(checkoutActions.updateStateBasedOnLocation())

        /*

            If user's current location currency is inactive by admin then the currency in which the page 
            was created will be displayed in currency dropdown and user's default currency will be displayed
            in contact info form
            If both user's current location currency and page's currency are inactive by admin then
            the Pakistan's currency will be displayed in currency dropdown and user's default currency
            in contact info form

        */
        else if (pageCurrencyExistsInDB && !userLocationCurrencyExistsInDB && isFundraiserCheckout)
            dispatch(checkoutActions.updateStateBasedOnPageDetails(pageDetails))

        else dispatch(checkoutActions.updateDonationMessage(pageDetails.donation_message))

    }, [userLocationCurrencyExistsInDB])



    return <Stack id="checkoutAmountSection" spacing={1}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>Select your currency</Typography>
        <Typography gutterBottom>Please select the currency in which you want to give by selecting its country and enter the amount you’re willing to give</Typography>

        <GoalAmountInput
            selectedCurrencyOption={state.selectedCountry.name}
            currencyChangeHandler={updateSelectedCurrency}
            amountProps={{
                id: "donationAmount",
                name: 'donationAmount',
                type: 'number',
                placeholder: '0',
                value: Number(state.donationAmount),
                onChange: handleChange,
                error: displayError(validationErrors.donationAmount),
                helpertext: validationErrors.donationAmount
            }}
        />

        <CheckboxWithLabel nameAndId={"isRecurring"} label="Make this a monthly recurring gift" handleCheck={handleChange} />
        <MessageBox message={donationMessage} />
        <DefaultAmountOptions handleAmountChange={handleChange} donationAmount={state.donationAmount} />
        <DonationTypesComponent donationType={state.donationType} updateDonationType={handleChange} />
    </Stack>
}

export default function ({ isFundraiserCheckout, handleChange, validationErrors, pageDetails }) {
    const state = useSelector(state => state.donationCheckout);

    return useMemo(() => {
        return <AmountSection isFundraiserCheckout={isFundraiserCheckout} state={state} handleChange={handleChange} validationErrors={validationErrors} pageDetails={pageDetails} />
    }, [state.donationAmount,
    state.selectedCountry.id,
    state.isRecurring,
    state.donationType,
    validationErrors.donationAmount])
}